import React, { useState } from 'react';
import { createPortal } from 'react-dom';

import {
  DndContext,
  DragEndEvent,
  DragOverlay,
  DragStartEvent,
  MouseSensor,
  TouchSensor,
  UniqueIdentifier,
  closestCenter,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  SortableContext,
  arrayMove,
  rectSortingStrategy,
} from '@dnd-kit/sortable';
import { DemmiFS } from '@subhanhabib/demmilib';

import GalleryEditorImage from './GalleryEditorImage';
import GalleryEditorImageSortable from './GalleryEditorImageSortable';

interface Props {
  isVendor?: boolean;
  images: DemmiFS.DemmiImageResource[];
  setImages: React.Dispatch<React.SetStateAction<DemmiFS.DemmiImageResource[]>>;
  onClickDelete?: (index: number) => void;
}

const GalleryEditor: React.FC<Props> = ({
  isVendor,
  images,
  setImages,
  onClickDelete,
}) => {
  const CSSBlock = 'gallery-editor';
  const [activeID, setActiveID] = useState<UniqueIdentifier | null>(null);
  const sensors = useSensors(useSensor(MouseSensor), useSensor(TouchSensor));

  function handleDragStart(event: DragStartEvent) {
    // console.log('drag start', event.active.id);
    setActiveID(event.active.id);
  }

  function handleDragEnd({ active, over }: DragEndEvent) {
    // console.log('drag end', active, over);

    if (over && active.id !== over.id) {
      setImages(images => {
        const oldIndex = images.findIndex(i => i.thumbnail === active.id);
        const newIndex = images.findIndex(i => i.thumbnail === over.id);
        const newArray = arrayMove(images, oldIndex, newIndex);
        // console.log(oldIndex, newIndex, newArray);
        return newArray;
      });
    }

    setActiveID(null);
  }

  function handleDragCancel() {
    // console.log('handleDragCancel');
    setActiveID(null);
  }

  return (
    <div className={CSSBlock}>
      <DndContext
        sensors={sensors}
        collisionDetection={closestCenter}
        onDragStart={handleDragStart}
        onDragEnd={handleDragEnd}
        onDragCancel={handleDragCancel}>
        <SortableContext
          items={images.map(d => d.thumbnail)}
          strategy={rectSortingStrategy}>
          <div className={`${CSSBlock}__thumbnails`}>
            {images.map((image, index) => (
              <GalleryEditorImageSortable
                key={image.thumbnail}
                image={image}
                index={index}
                CSSBlock={CSSBlock}
                isVendor={isVendor}
                onClickDelete={onClickDelete}
              />
            ))}
          </div>
        </SortableContext>

        {createPortal(
          <DragOverlay adjustScale={true}>
            {activeID ? (
              <GalleryEditorImage
                isOverlay
                image={images.find(f => f.thumbnail === activeID)}
                index={images.findIndex(f => f.thumbnail === activeID)}
                CSSBlock={CSSBlock}
                isVendor={isVendor}
              />
            ) : null}
          </DragOverlay>,
          document.body,
        )}
      </DndContext>
    </div>
  );
};

export default GalleryEditor;
